import React, { useEffect, useState } from 'react';
import { Mail, Monitor, Bell } from 'lucide-react';
import {
  notificationService,
  NOTIFICATION_GROUPS,
  NotificationGroup,
  PrefChannel,
} from '../services/notificationService';
import { supabase } from '../services/supabaseService';

type PrefsMap = Record<NotificationGroup, Record<PrefChannel, boolean>>;

const CHANNELS: Array<{ id: PrefChannel; label: string; icon: React.ComponentType<{ className?: string }>; description: string }> = [
  { id: 'in_app', label: 'En la campana', icon: Monitor, description: 'Aparece en la campana del CRM' },
  { id: 'email', label: 'Email', icon: Mail, description: 'Enviamos un correo cuando ocurre' },
];

const buildDefaults = (): PrefsMap =>
  NOTIFICATION_GROUPS.reduce((acc, g) => {
    acc[g.key] = { in_app: true, email: true };
    return acc;
  }, {} as PrefsMap);

const NotificationPreferences: React.FC = () => {
  const [prefs, setPrefs] = useState<PrefsMap>(buildDefaults());
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        setUserId(user.id);

        const rows = await notificationService.getUserPreferences(user.id);
        const next = buildDefaults();
        rows.forEach((r) => {
          if (next[r.groupKey]) next[r.groupKey][r.channel] = r.enabled;
        });
        setPrefs(next);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = async (group: NotificationGroup, channel: PrefChannel) => {
    if (!userId) return;
    const key = `${group}:${channel}`;
    const nextValue = !prefs[group][channel];

    setPrefs((prev) => ({ ...prev, [group]: { ...prev[group], [channel]: nextValue } }));
    setPending(key);
    try {
      await notificationService.updateUserPreference(userId, group, channel, nextValue);
    } catch (err) {
      setPrefs((prev) => ({ ...prev, [group]: { ...prev[group], [channel]: !nextValue } }));
      console.error('Error actualizando preferencia', err);
    } finally {
      setPending(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Preferencias de notificaciones</h2>
        <p className="text-sm text-gray-600">
          Elige qué tipos de eventos quieres recibir y por qué canal. Los cambios se guardan al momento.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {CHANNELS.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Icon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{c.label}</p>
                <p className="text-xs text-gray-600">{c.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
        {NOTIFICATION_GROUPS.map((group) => (
          <div key={group.key} className="p-5 flex items-start justify-between gap-6">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{group.label}</p>
              <p className="text-xs text-gray-600 mt-0.5">{group.description}</p>
            </div>
            <div className="flex items-center gap-6">
              {CHANNELS.map((c) => {
                const Icon = c.icon;
                const value = prefs[group.key][c.id];
                const pendingKey = `${group.key}:${c.id}`;
                return (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={value}
                      disabled={pending === pendingKey}
                      onChange={() => toggle(group.key, c.id)}
                    />
                    <Icon className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-700">{c.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-start gap-2 text-xs text-gray-600">
        <Bell className="w-4 h-4 mt-0.5 text-gray-500" />
        <p>
          Los avisos “en la campana” también generan una notificación del navegador si diste permiso. El canal
          email sólo envía correos transaccionales importantes; nunca marketing.
        </p>
      </div>
    </div>
  );
};

export default NotificationPreferences;
